import { getAuthHeaders, authenticatedFetch } from './authApi';
import { getCachedOrFetch, invalidateCachedValue } from './requestCache';
import { getEnv } from '../utils/env';

type UnknownRecord = Record<string, unknown>;

const toApiBaseUrl = (): string => getEnv('VITE_AI_API_URL', '').replace(/\/$/, '');
const CLUB_DETAIL_TTL_MS = 60_000;

export const stageClubLogoApi = async (file: File, clubId?: string): Promise<string> => {
  const body = new FormData();
  body.append('file', file);
  const query = clubId ? `?club_id=${encodeURIComponent(clubId)}` : '';
  const response = await authenticatedFetch(`${toApiBaseUrl()}/api/v1/clubs/logo-assets${query}`, { method: 'POST', body });
  if (!response.ok) throw new Error('Logo upload failed');
  return (await response.json()).data.logo_url;
};

export const uploadMissingClubLogoApi = async (clubId: string, file: File): Promise<string> => {
  const body = new FormData();
  body.append('file', file);
  const response = await authenticatedFetch(`${toApiBaseUrl()}/api/v1/clubs/${encodeURIComponent(clubId)}/logo`, { method: 'POST', body });
  if (!response.ok) throw new Error(response.status === 409 ? 'logo_exists' : 'logo_upload_failed');
  const payload = await response.json();
  invalidateCachedValue('clubs:');
  window.dispatchEvent(new CustomEvent('club-logo-updated', { detail: { clubId, logoUrl: payload.data.logo_url } }));
  return payload.data.logo_url;
};

export class ClubsApiError extends Error {
  status: number;
  payload: unknown;
  path: string;

  constructor(message: string, status: number, payload: unknown, path: string) {
    super(message);
    this.name = 'ClubsApiError';
    this.status = status;
    this.payload = payload;
    this.path = path;
  }
}

const parseJson = async (response: Response): Promise<unknown> => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const extractMessage = (payload: unknown, fallback: string): string => {
  if (payload && typeof payload === 'object' && 'detail' in (payload as UnknownRecord)) {
    const detailValue = (payload as UnknownRecord).detail;
    if (typeof detailValue === 'string' && detailValue.trim().length > 0) return detailValue;
    if (detailValue && typeof detailValue === 'object') {
      if ('message' in (detailValue as UnknownRecord)) {
        const message = (detailValue as UnknownRecord).message;
        if (typeof message === 'string' && message.trim().length > 0) return message;
      }
      if ('error' in (detailValue as UnknownRecord)) {
        const detailError = (detailValue as UnknownRecord).error;
        if (typeof detailError === 'string' && detailError.trim().length > 0) return detailError;
      }
    }
  }
  if (payload && typeof payload === 'object' && 'error' in (payload as UnknownRecord)) {
    const errorValue = (payload as UnknownRecord).error;
    if (typeof errorValue === 'string' && errorValue.trim().length > 0) return errorValue;
    if (errorValue && typeof errorValue === 'object' && 'message' in (errorValue as UnknownRecord)) {
      const msg = (errorValue as UnknownRecord).message;
      if (typeof msg === 'string' && msg.trim().length > 0) return msg;
    }
  }
  if (payload && typeof payload === 'object' && 'message' in (payload as UnknownRecord)) {
    const message = (payload as UnknownRecord).message;
    if (typeof message === 'string' && message.trim().length > 0) return message;
  }
  return fallback;
};

const extractData = (payload: unknown): unknown => {
  if (payload && typeof payload === 'object' && 'data' in (payload as UnknownRecord)) {
    return (payload as UnknownRecord).data;
  }
  return payload;
};

const clubsRequest = async (path: string, options: RequestInit = {}): Promise<unknown> => {
  const baseUrl = toApiBaseUrl();
  if (!baseUrl) {
    throw new Error('VITE_AI_API_URL is not configured');
  }

  const response = await authenticatedFetch(`${baseUrl}${path}`, {
    ...options,
    credentials: 'include',
    headers: getAuthHeaders(options.headers || {}),
  });

  const payload = await parseJson(response);
  if (!response.ok) {
    throw new ClubsApiError(
      extractMessage(payload, `Clubs API request failed (${response.status})`),
      response.status,
      payload,
      path
    );
  }

  return extractData(payload);
};

export const listClubsApi = async (params?: {
  fields?: string;
  q?: string;
  limit?: number;
  offset?: number;
}): Promise<unknown[]> => {
  const query = new URLSearchParams();
  if (params?.fields) query.set('fields', params.fields);
  if (params?.q) query.set('q', params.q);
  if (typeof params?.limit === 'number') query.set('limit', String(params.limit));
  if (typeof params?.offset === 'number') query.set('offset', String(params.offset));
  const qs = query.toString();
  const payload = await clubsRequest(`/api/v1/clubs${qs ? `?${qs}` : ''}`, { method: 'GET' });
  return Array.isArray(payload) ? payload : [];
};

export const getClubApi = async (clubId: string): Promise<unknown | null> => {
  return await getCachedOrFetch(
    `clubs:get:${clubId}`,
    CLUB_DETAIL_TTL_MS,
    async () => {
      const payload = await clubsRequest(`/api/v1/clubs/${encodeURIComponent(clubId)}`, { method: 'GET' });
      return payload ?? null;
    }
  );
};

export const getClubNameApi = async (clubId: string): Promise<{ id: string; name: string } | null> => {
  return await getCachedOrFetch(
    `clubs:name:${clubId}`,
    CLUB_DETAIL_TTL_MS,
    async () => {
      const payload = await clubsRequest(`/api/v1/clubs/${encodeURIComponent(clubId)}/name`, { method: 'GET' });
      if (!payload || typeof payload !== 'object') return null;
      const record = payload as UnknownRecord;
      return {
        id: String(record.id || ''),
        name: String(record.name || ''),
      };
    }
  );
};

export const getClubLogoApi = async (clubId: string): Promise<{ id: string; logo_url: string | null } | null> => {
  return await getCachedOrFetch(
    `clubs:logo:${clubId}`,
    CLUB_DETAIL_TTL_MS,
    async () => {
      const payload = await clubsRequest(`/api/v1/clubs/${encodeURIComponent(clubId)}/logo`, { method: 'GET' });
      if (!payload || typeof payload !== 'object') return null;
      const record = payload as UnknownRecord;
      return {
        id: String(record.id || ''),
        logo_url: typeof record.logo_url === 'string' ? record.logo_url : null,
      };
    }
  );
};

export const createClubApi = async (payload: Record<string, unknown>): Promise<unknown> => {
  const result = await clubsRequest('/api/v1/clubs', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  invalidateCachedValue('clubs:');
  return result;
};

export const updateClubApi = async (clubId: string, payload: Record<string, unknown>): Promise<unknown> => {
  const result = await clubsRequest(`/api/v1/clubs/${encodeURIComponent(clubId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  invalidateCachedValue(`clubs:get:${clubId}`);
  invalidateCachedValue(`clubs:name:${clubId}`);
  invalidateCachedValue(`clubs:logo:${clubId}`);
  if ('logo_url' in payload) window.dispatchEvent(new CustomEvent('club-logo-updated', { detail: { clubId, logoUrl: payload.logo_url || '' } }));
  return result;
};

export const deleteClubApi = async (
  clubId: string,
  options?: {
    profileCleanup?: 'detach' | 'delete_users';
    forceFullDelete?: boolean;
    forceConfirmName?: string;
  }
): Promise<void> => {
  const query = new URLSearchParams();
  if (options?.profileCleanup) query.set('profile_cleanup', options.profileCleanup);
  if (options?.forceFullDelete) query.set('force_full_delete', 'true');
  if (options?.forceConfirmName) query.set('force_confirm_name', options.forceConfirmName);
  const qs = query.toString();
  await clubsRequest(`/api/v1/clubs/${encodeURIComponent(clubId)}${qs ? `?${qs}` : ''}`, {
    method: 'DELETE',
  });
  invalidateCachedValue(`clubs:get:${clubId}`);
  invalidateCachedValue(`clubs:name:${clubId}`);
  invalidateCachedValue(`clubs:logo:${clubId}`);
};

export interface ClubDeleteDependency {
  table: string;
  column: string;
  label: string;
  count: number;
  emails?: string[];
}

export interface ClubDeleteCheck {
  club_id: string;
  can_delete: boolean;
  dependencies: ClubDeleteDependency[];
  profile_cleanup?: 'detach' | 'delete_users';
}

export const getClubDeleteCheckApi = async (clubId: string): Promise<ClubDeleteCheck> => {
  const payload = await clubsRequest(`/api/v1/clubs/${encodeURIComponent(clubId)}/delete-check`, {
    method: 'GET',
  });
  const record = payload && typeof payload === 'object' ? (payload as UnknownRecord) : {};
  const dependencies = Array.isArray(record.dependencies)
    ? record.dependencies
      .filter((item): item is UnknownRecord => Boolean(item) && typeof item === 'object')
      .map((item) => ({
        table: typeof item.table === 'string' ? item.table : '',
        column: typeof item.column === 'string' ? item.column : '',
        label: typeof item.label === 'string' ? item.label : '',
        count: typeof item.count === 'number' ? item.count : 0,
        emails: Array.isArray(item.emails)
          ? item.emails.filter((email): email is string => typeof email === 'string' && email.trim().length > 0)
          : undefined,
      }))
      .filter((item) => item.table.length > 0)
    : [];

  return {
    club_id: typeof record.club_id === 'string' ? record.club_id : clubId,
    can_delete: Boolean(record.can_delete),
    profile_cleanup: record.profile_cleanup === 'delete_users' ? 'delete_users' : 'detach',
    dependencies,
  };
};

export const getClubsByIdsApi = async (clubIds: string[]): Promise<Array<{ id: string; name: string }>> => {
  const uniqueIds = Array.from(new Set((clubIds || []).filter(Boolean)));
  if (uniqueIds.length === 0) return [];
  const rows = await Promise.all(uniqueIds.map(async (clubId) => {
    try {
      return await getClubNameApi(clubId);
    } catch {
      return null;
    }
  }));
  return rows.filter((row): row is { id: string; name: string } => !!row && !!row.id);
};

export interface ClubStatistics {
  club_id: string;
  teams_count: number;
  users_count: number;
  coaches_and_managers_count: number;
  players_count: number;
  last_activity_at: string | null;
  active_coaches_30d: number;
  active_users_30d: number;
  user_sign_ins_30d: number;
  coach_sign_ins_30d: number;
  teams_by_season: { season_id: string | null; season_name: string | null; teams_count: number }[];
}

export const getClubStatisticsApi = async (): Promise<ClubStatistics[]> => {
  const data = await clubsRequest('/api/v1/clubs/statistics');
  if (!Array.isArray(data) || data.some(row => !row || typeof row.club_id !== 'string'
    || !Number.isInteger(row.teams_count) || row.teams_count < 0
    || !Number.isInteger(row.users_count) || row.users_count < 0
    || !Number.isInteger(row.coaches_and_managers_count) || row.coaches_and_managers_count < 0
    || !Number.isInteger(row.players_count) || row.players_count < 0
    || !Number.isInteger(row.active_coaches_30d) || row.active_coaches_30d < 0
    || !Number.isInteger(row.active_users_30d) || row.active_users_30d < 0 || row.active_users_30d > row.users_count
    || !Number.isInteger(row.user_sign_ins_30d) || row.user_sign_ins_30d < 0
    || !Number.isInteger(row.coach_sign_ins_30d) || row.coach_sign_ins_30d < 0
    || (row.last_activity_at !== null && (typeof row.last_activity_at !== 'string' || !Number.isFinite(Date.parse(row.last_activity_at))))
    || !Array.isArray(row.teams_by_season) || row.teams_by_season.some((season: UnknownRecord) =>
      !season || (season.season_id !== null && typeof season.season_id !== 'string')
      || (season.season_name !== null && typeof season.season_name !== 'string')
      || !Number.isInteger(season.teams_count) || Number(season.teams_count) < 0))) {
    throw new Error('Invalid club statistics response');
  }
  return data as ClubStatistics[];
};
